import { Checkbox as CheckboxPrimitive } from "@base-ui/react/checkbox";
import { cva, type VariantProps } from "class-variance-authority";
import { useId } from "react";
import { cn } from "@/lib/utils";

const checkboxFrameRadiusClasses = {
  sm: "rounded-[2px]",
  md: "rounded-(--radius-sm)",
} as const;

const checkboxVisualSizeClasses = {
  sm: ["w-[calc(var(--icon-sm)*0.8)]", "h-[calc(var(--icon-sm)*0.8)]"],
  // Snap md to whole pixels to avoid SVG crossfade shimmer from 19.2px subpixel rasterization.
  md: ["w-[20px]", "h-[20px]"],
} as const;

const boxVariants = cva(
  [
    "relative",
    "shrink-0",
    "inline-flex",
    "items-center",
    "justify-center",
    "group/checkbox",
    // disabled
    "data-[disabled]:pointer-events-none",
    // focus
    "focus-visible:outline-none",
    "focus-visible:ring-[1px]",
    "focus-visible:ring-checkbox-focus-ring",
    "cursor-[inherit]",
    // touch target — before:inset per size to maintain WCAG 44×44px minimum
    "[@media(pointer:coarse)]:before:content-['']",
    "[@media(pointer:coarse)]:before:absolute",
  ],
  {
    variants: {
      size: {
        sm: [
          "w-(--icon-sm)",
          "h-(--icon-sm)",
          checkboxFrameRadiusClasses.sm,
          "[@media(pointer:coarse)]:before:inset-[-12px]",
        ],
        md: [
          "w-(--icon-md)",
          "h-(--icon-md)",
          checkboxFrameRadiusClasses.md,
          "[@media(pointer:coarse)]:before:inset-[-10px]",
        ],
      },
    },
    defaultVariants: {
      size: "sm",
    },
  },
);

const visualVariants = cva(
  [
    "absolute",
    "left-1/2",
    "top-1/2",
    "z-10",
    "inline-flex",
    "-translate-x-1/2",
    "-translate-y-1/2",
    "items-center",
    "justify-center",
    "overflow-hidden",
    "transition-[background-color]",
    "duration-200",
    "ease-in-out",
    "motion-reduce:transition-none",
    "group-data-[checked]/checkbox:bg-checkbox-filled-default-background",
    "group-data-[indeterminate]/checkbox:bg-checkbox-filled-default-background",
    "group-[[data-checked]:not([data-error]):not([data-disabled]):hover]/checkbox:bg-checkbox-filled-hover-background",
    "group-[[data-indeterminate]:not([data-error]):not([data-disabled]):hover]/checkbox:bg-checkbox-filled-hover-background",
    "group-[[data-checked]:not([data-error]):not([data-disabled]):active]/checkbox:bg-checkbox-filled-pressed-background",
    "group-[[data-indeterminate]:not([data-error]):not([data-disabled]):active]/checkbox:bg-checkbox-filled-pressed-background",
    "group-data-[error]/checkbox:bg-error",
    "group-data-[disabled]/checkbox:bg-checkbox-filled-disabled-background",
  ],
  {
    variants: {
      size: {
        sm: [checkboxFrameRadiusClasses.sm, ...checkboxVisualSizeClasses.sm],
        md: [checkboxFrameRadiusClasses.md, ...checkboxVisualSizeClasses.md],
      },
    },
    defaultVariants: {
      size: "sm",
    },
  },
);

const hoverFrameVariants = cva(
  [
    "absolute",
    "inset-0",
    "rounded-(--radius-sm)",
    "opacity-0",
    "group-[:not([data-checked]):not([data-indeterminate]):not([data-error]):not([data-disabled]):hover]/checkbox:opacity-100",
    "group-[:not([data-checked]):not([data-indeterminate]):not([data-error]):not([data-disabled]):hover]/checkbox:bg-checkbox-outline-hover-background",
    "transition-[background-color,opacity]",
    "duration-200",
    "ease-in-out",
    "motion-reduce:transition-none",
  ],
  {
    variants: {
      size: {
        sm: ["w-(--icon-sm)", "h-(--icon-sm)"],
        md: ["w-(--icon-md)", "h-(--icon-md)"],
      },
    },
    defaultVariants: {
      size: "sm",
    },
  },
);

const pressedFrameVariants = cva(
  [
    "absolute",
    "inset-0",
    "rounded-(--radius-sm)",
    "opacity-0",
    "group-[:not([data-checked]):not([data-indeterminate]):not([data-error]):not([data-disabled]):active]/checkbox:opacity-100",
    "group-[:not([data-checked]):not([data-indeterminate]):not([data-error]):not([data-disabled]):active]/checkbox:bg-checkbox-outline-pressed-background",
    "transition-[background-color,opacity]",
    "duration-200",
    "ease-in-out",
    "motion-reduce:transition-none",
  ],
  {
    variants: {
      size: {
        sm: ["w-(--icon-sm)", "h-(--icon-sm)"],
        md: ["w-(--icon-md)", "h-(--icon-md)"],
      },
    },
    defaultVariants: {
      size: "sm",
    },
  },
);

const uncheckedIconVariants = cva([
  "absolute",
  "inset-0",
  "h-full",
  "w-full",
  "text-checkbox-outline-default-border",
  "group-[:not([data-error]):not([data-disabled]):hover]/checkbox:text-checkbox-outline-hover-border",
  "group-[:not([data-error]):not([data-disabled]):active]/checkbox:text-checkbox-outline-pressed-border",
  "group-data-[error]/checkbox:text-error",
  "group-data-[disabled]/checkbox:text-checkbox-filled-disabled-background",
  "group-data-[checked]/checkbox:opacity-0",
  "group-data-[indeterminate]/checkbox:opacity-0",
  "transition-[color,opacity]",
  "duration-200",
  "ease-in-out",
  "motion-reduce:transition-none",
]);

const checkedIconVariants = cva([
  "absolute",
  "inset-0",
  "h-full",
  "w-full",
  "opacity-0",
  "text-primary-on-primary",
  "group-data-[checked]/checkbox:opacity-100",
  "group-data-[error]/checkbox:text-surface-container-lowest",
  "group-data-[disabled]/checkbox:text-surface-container-lowest",
  "transition-[color,opacity]",
  "duration-200",
  "ease-in-out",
  "motion-reduce:transition-none",
]);

const indeterminateIconVariants = cva([
  "absolute",
  "inset-0",
  "h-full",
  "w-full",
  "opacity-0",
  "text-primary-on-primary",
  "group-data-[indeterminate]/checkbox:opacity-100",
  "group-data-[error]/checkbox:text-surface-container-lowest",
  "group-data-[disabled]/checkbox:text-surface-container-lowest",
  "transition-[color,opacity]",
  "duration-200",
  "ease-in-out",
  "motion-reduce:transition-none",
]);

type CheckboxIconProps = {
  className?: string;
};

function UncheckedIcon({ className }: CheckboxIconProps) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <path
        d="M13.7143 1.14286C14.3464 1.14286 14.8571 1.65357 14.8571 2.28571V13.7143C14.8571 14.3464 14.3464 14.8571 13.7143 14.8571H2.28571C1.65357 14.8571 1.14286 14.3464 1.14286 13.7143V2.28571C1.14286 1.65357 1.65357 1.14286 2.28571 1.14286H13.7143ZM2.28571 0C1.025 0 0 1.025 0 2.28571V13.7143C0 14.975 1.025 16 2.28571 16H13.7143C14.975 16 16 14.975 16 13.7143V2.28571C16 1.025 14.975 0 13.7143 0H2.28571Z"
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
      />
    </svg>
  );
}

function CheckedIcon({ className }: CheckboxIconProps) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <path
        d="M12.2143 4.06071C11.8321 3.78214 11.2964 3.86786 11.0179 4.25L6.75357 10.1143L4.89286 8.25357C4.55714 7.91786 4.01429 7.91786 3.68214 8.25357C3.35 8.58929 3.34643 9.13214 3.68214 9.46429L6.25357 12.0357C6.43214 12.2143 6.67857 12.3036 6.925 12.2857C7.17143 12.2679 7.40357 12.1393 7.55 11.9357L12.4036 5.25714C12.6821 4.875 12.5964 4.33929 12.2143 4.06071Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IndeterminateIcon({ className }: CheckboxIconProps) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <path d="M3 7V9H13V7H3Z" fill="currentColor" />
    </svg>
  );
}

const labelTextStyles = cva([
  "text-text-default",
  "font-(family-name:--font-family-label)",
  "font-(number:--font-weight-medium)",
  "text-(length:--font-size-label-large)",
  "leading-(--font-line-height-20)",
  "tracking-(--font-letter-spacing-default)",
]);

const descriptionStyles = cva([
  "text-text-subtext",
  "font-(family-name:--font-family-body)",
  "font-(number:--font-weight-regular)",
  "text-(length:--font-size-body-medium)",
  "leading-(--font-line-height-20)",
  "tracking-(--font-letter-spacing-default)",
]);

// Indents description to align with label text in the Default layout. No indentation in Left Text layout.
const descriptionPaddingVariants = cva([], {
  variants: {
    size: {
      sm: [],
      md: [],
    },
    labelLeft: {
      true: [],
      false: [],
    },
  },
  compoundVariants: [
    {
      labelLeft: false,
      size: "sm",
      className: "pl-[calc(var(--icon-sm)+var(--spacing-8))]",
    },
    {
      labelLeft: false,
      size: "md",
      className: "pl-[calc(var(--icon-md)+var(--spacing-8))]",
    },
  ],
  defaultVariants: {
    size: "sm",
    labelLeft: false,
  },
});

export type CheckboxProps = VariantProps<typeof boxVariants> &
  Omit<
    CheckboxPrimitive.Root.Props,
    "className" | "aria-label" | "aria-labelledby"
  > & {
    error?: boolean;
    description?: string;
    /** Renders the label on the left and control on the right. @default false */
    labelLeft?: boolean;
  } & (
    | { label: string; "aria-label"?: never }
    | { label?: never; "aria-label": string }
  );

/**
 * Checkbox control for selecting one or more independent options.
 * Supports selected, unselected, and indeterminate states.
 *
 * @example
 * <Checkbox label="Label" />
 * <Checkbox label="Label" description="Description" />
 * <Checkbox labelLeft label="Label" description="Description" />
 * <Checkbox checked={checked} onCheckedChange={setChecked} label="Label" />
 */
function Checkbox({
  error,
  label,
  description,
  labelLeft,
  size,
  indeterminate,
  disabled,
  ...props
}: CheckboxProps) {
  const id = useId();
  const descriptionIds =
    [props["aria-describedby"], description ? `${id}-description` : undefined]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: Base UI renders the interactive checkbox control inside this label.
    <label
      className={cn(
        "inline-flex flex-col gap-(--spacing-4)",
        disabled ? "cursor-not-allowed" : "cursor-pointer",
      )}
    >
      <div className="inline-flex items-center gap-(--spacing-8)">
        {labelLeft && label && (
          <span
            id={`${id}-label`}
            className={cn(labelTextStyles(), disabled && "opacity-50")}
          >
            {label}
          </span>
        )}

        <CheckboxPrimitive.Root
          {...props}
          indeterminate={indeterminate}
          disabled={disabled}
          aria-label={label ? undefined : props["aria-label"]}
          aria-labelledby={label ? `${id}-label` : undefined}
          aria-describedby={descriptionIds}
          aria-invalid={error || undefined}
          data-error={error ? "" : undefined}
          className={cn(boxVariants({ size }))}
        >
          <span className={cn(hoverFrameVariants({ size }))} />
          <span className={cn(pressedFrameVariants({ size }))} />
          <span className={cn(visualVariants({ size }))}>
            <UncheckedIcon className={cn(uncheckedIconVariants())} />
            <CheckedIcon className={cn(checkedIconVariants())} />
            <IndeterminateIcon className={cn(indeterminateIconVariants())} />
          </span>
        </CheckboxPrimitive.Root>

        {!labelLeft && label && (
          <span
            id={`${id}-label`}
            className={cn(labelTextStyles(), disabled && "opacity-50")}
          >
            {label}
          </span>
        )}
      </div>

      {description && (
        <span
          id={`${id}-description`}
          className={cn(
            descriptionStyles(),
            disabled && "opacity-50",
            descriptionPaddingVariants({ size, labelLeft }),
          )}
        >
          {description}
        </span>
      )}
    </label>
  );
}

export { Checkbox };
